import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { Competition } from '../../../../../model/competitions/Competition';
import { HockeyCategory } from '../../../../../model/competitions/HockeyCategory';
import { HockeyPaymentModel } from '../../../../../model/enum/HockeyPaymentModel';
import { CompetitionService } from '../../../../../services/competition.service';
import { LoadingService } from '../../../../../services/loading.service';

@Component({
  selector: 'app-hockey-inscription-fee-modal',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    InputNumberModule,
    TagModule,
    FormsModule
  ],
  providers: [MessageService],
  templateUrl: './hockey-inscription-fee-modal.component.html',
  styleUrl: './hockey-inscription-fee-modal.component.scss',
})
export class HockeyInscriptionFeeModalComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() competition!: Competition;
  @Input() category!: HockeyCategory;
  @Input() paymentModel: HockeyPaymentModel = HockeyPaymentModel.PER_TEAM;

  @Output() onClose = new EventEmitter<boolean>();
  @Output() onSaveSuccess = new EventEmitter<number>();
  inscriptionFee: number = 0;
  originalFee: number = 0;

  constructor(
    private competitionService: CompetitionService,
    private loadingService: LoadingService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadCurrentFee();
  }

  ngOnChanges(): void {
    if (this.visible && this.category) {
      this.loadCurrentFee();
    }
  }

  private loadCurrentFee(): void {
    if (!this.competition || !this.category) return;

    // Get current fee from customData if exists
    const customData = this.competition.customData || {};
    const categoryFees = (customData as any).inscriptionFees || {};
    this.inscriptionFee = categoryFees[this.category.id] || 0;
    this.originalFee = this.inscriptionFee;
  }

  onSave(): void {
    if (this.inscriptionFee < 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Valor inválido',
        detail: 'El valor de inscripción no puede ser negativo',
        life: 3000,
      });
      return;
    }

    this.loadingService.showLoading();

    // Prepare updated competition data
    const updatedCompetition = { ...this.competition };
    
    // Initialize customData if it doesn't exist
    if (!updatedCompetition.customData) {
      updatedCompetition.customData = {} as any;
    }

    // Initialize inscriptionFees if it doesn't exist
    if (!(updatedCompetition.customData as any).inscriptionFees) {
      (updatedCompetition.customData as any).inscriptionFees = {};
    }

    // Update the fee for this category
    (updatedCompetition.customData as any).inscriptionFees[this.category.id] = this.inscriptionFee;

    // Save to backend - you'll need to implement the update method in CompetitionService
    this.competitionService.updateCompetition(updatedCompetition).subscribe({
      next: (response) => {
        this.loadingService.hideLoading();
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: `Valor de inscripción actualizado para ${this.category.name}`,
          life: 3000,
        });
        this.originalFee = this.inscriptionFee;
        this.onClose.emit(true);
      },
      error: (error) => {
        this.loadingService.hideLoading();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo actualizar el valor de inscripción',
          life: 5000,
        });
      }
    });
  }

  onCloseModal(saved: boolean = false): void {
    if (!saved) {
      this.inscriptionFee = this.originalFee; // Reset to original value
    }
    this.onClose.emit(saved);
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  }

  get hasChanges(): boolean {
    return this.inscriptionFee !== this.originalFee;
  }
}